/**
 * Script Helper Functions
 * Utilities for calculating body lines, word counts, and timing based on video duration
 */

/**
 * Get the number of body lines needed based on video duration
 * @param {number} durationSec - Video duration in seconds
 * @param {Object} mapping - Optional custom mapping (defaults to standard mapping)
 * @returns {number} Number of body lines
 */
export function getBodyLineCount(durationSec, mapping = null) {
  const defaultMapping = [
    { max: 20, count: 2 },
    { max: 30, count: 4 },
    { max: 45, count: 5 },
    { max: 60, count: 6 },
    { max: 80, count: 8 }
  ];
  
  const activeMapping = mapping || defaultMapping;
  for (const rule of activeMapping) {
    if (durationSec <= rule.max) {
      return rule.count;
    }
  }
  return activeMapping[activeMapping.length - 1].count; // Return max for longer videos
}

/**
 * Get word count range estimate based on duration
 * @param {number} durationSec - Video duration in seconds
 * @param {number} wps - Words per second rate (default 2.5)
 * @returns {{min: number, max: number, label: string}} Word count range
 */
export function getWordRange(durationSec, wps = 2.5) {
  const min = Math.round(durationSec * (wps - 0.2));
  const max = Math.round(durationSec * (wps + 0.2));
  return {
    min,
    max,
    label: `${min}–${max} words`
  };
}

/**
 * Get the time allocation per body line
 * @param {number} durationSec - Video duration in seconds
 * @param {number} bodyLineCount - Number of body lines
 * @param {number} hookTime - Time for hook (default 3)
 * @param {number} ctaTime - Time for CTA (default 4)
 * @returns {number} Seconds per body line
 */
export function getPerBodyTime(durationSec, bodyLineCount, hookTime = 3, ctaTime = 4) {
  const bodyTotal = durationSec - hookTime - ctaTime;
  const perBody = bodyTotal / bodyLineCount;
  return Math.round(perBody);
}

/**
 * Get word count estimate for a single body line
 * @param {number} perBodySeconds - Seconds allocated to this line
 * @param {number} wps - Words per second rate (default 2.5)
 * @returns {{min: number, max: number}} Word count range for this line
 */
export function getPerLineWordRange(perBodySeconds, wps = 2.5) {
  const min = Math.round(perBodySeconds * (wps - 0.2));
  const max = Math.round(perBodySeconds * (wps + 0.2));
  return { min, max };
}

/**
 * Count words in a text string (spaces-based for i18n)
 * @param {string} text - Text to count
 * @returns {number} Word count
 */
export function countWords(text) {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(word => word.length > 0).length;
}

/**
 * Validate duration and snap to nearest valid value
 * @param {number} durationSec - Proposed duration
 * @returns {number} Valid duration from supported set
 */
export function snapDuration(durationSec) {
  const validDurations = [15, 20, 30, 45, 60, 80];
  return validDurations.reduce((prev, curr) => {
    return Math.abs(curr - durationSec) < Math.abs(prev - durationSec) ? curr : prev;
  });
}




